import useSWR, { SWRResponse } from "swr";

import { useAuth, useSWRApiErrorHandler } from "../common/hooks";
import { ReturnClaimFormValues } from "../features/architect/hooks/useReturnClaimHandler";
import { ClaimFormValues } from "../features/claim/components/ClaimForm";
import { NegotiateClaimFormValues } from "../features/claim/hooks/useNegotiateClaimHandler";
import { RejectClaimFormValues } from "../features/claim/hooks/useRejectClaimHandler";
import { BesuClaimListItem, ClaimState, BesuClaimState, ClaimHistory } from "../types/claim";
import {
    StateAndRef,
    UploadedFile,
    UUID,
    WorkflowResult,
} from "../types/common";

import request, {
    AccessTokenHeader,
    ApiError,
    requestWithToken,
} from "./common";

const claimApi = {
    create: (
        projectId: string,
        values: ClaimFormValues,
    ): Promise<ClaimWorkflowResult> => {
        const payload: ClaimCreationRequest = {
            projectId,
            descriptionRef: values.description,
            attachedFiles: values.files.map(toClaimAttachedFile),
        };
        return request<ClaimWorkflowResult, ClaimCreationRequest>(
            "/claims/submit",
            "POST",
            payload,
        );
    },
    update: (
        ref: string,
        values: ClaimFormValues,
    ): Promise<ClaimWorkflowResult> => {
        const payload: ClaimUpdateRequest = {
            description: values.description,
            documents: values.files.map(toClaimAttachedFile),
        };
        return request<ClaimWorkflowResult, ClaimUpdateRequest>(
            `/claims/update/${ref}`,
            "PUT",
            payload,
        );
    },
    resubmit: (
        ref: string,
        values: ClaimFormValues,
    ): Promise<BesuClaimWorkflowResult> => {
        const payload: ClaimUpdateRequest = {
            description: values.description,
            documents: values.files.map(toClaimAttachedFile),
        };
        return request<BesuClaimWorkflowResult, ClaimUpdateRequest>(
            `/claims/resubmit/${ref}`,
            "POST",
            payload,
        );
    },
    discard: (ref: string): Promise<BesuClaimWorkflowResult> => {
        return request<BesuClaimWorkflowResult>(`/claims/discard/${ref}`, "POST");
    },
    architectEndorse: (
        ref: string,
        token: string,
    ): Promise<ClaimWorkflowResult> => {
        return request<ClaimWorkflowResult>(
            `/token-access/claims/architect/approve/${ref}`,
            "POST",
            undefined,
            {
                headers: {
                    [AccessTokenHeader]: token,
                },
            },
        );
    },
    architectReturn: (
        ref: string,
        values: ReturnClaimFormValues,
        token: string,
    ): Promise<ClaimWorkflowResult> => {
        const payload: ClaimReturnRequest = {
            comment: values.comment,
        };
        return request<ClaimWorkflowResult, ClaimReturnRequest>(
            `/token-access/claims/architect/reject/${ref}`,
            "POST",
            payload,
            {
                headers: {
                    [AccessTokenHeader]: token,
                },
            },
        );
    },
    poApprove: (ref: string): Promise<ClaimWorkflowResult> => {
        return request<ClaimWorkflowResult>(
            `/claims/po/approve/${ref}`,
            "POST",
        );
    },
    poNegotiate: (
        ref: string,
        values: NegotiateClaimFormValues,
    ): Promise<BesuClaimWorkflowResult> => {
        const payload: ClaimReturnRequest = {
            comment: values.comment,
        };
        return request<BesuClaimWorkflowResult, ClaimReturnRequest>(
            `/claims/po/reject/${ref}`,
            "POST",
            payload,
        );
    },
    claimApproverApprove: (ref: string): Promise<ClaimWorkflowResult> => {
        return request<ClaimWorkflowResult>(
            `/claims/approvers/approve/${ref}`,
            "POST",
        );
    },
    claimApproverReject: (
        ref: string,
        values: RejectClaimFormValues,
    ): Promise<BesuClaimWorkflowResult> => {
        const payload: ClaimReturnRequest = {
            comment: values.comment,
        };
        return request<BesuClaimWorkflowResult, ClaimReturnRequest>(
            `/claims/approvers/reject/${ref}`,
            "POST",
            payload,
        );
    },
    architectApprove: (
        ref: string,
        token: string,
    ): Promise<ClaimWorkflowResult> => {
        return request<ClaimWorkflowResult>(
            `/token-access/claims/approvers/approve/${ref}`,
            "POST",
            undefined,
            {
                headers: {
                    [AccessTokenHeader]: token,
                },
            },
        );
    },
};

const ZERO_ATTACHMENT_HASH = "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=";

const toClaimAttachedFile = (file: UploadedFile): ClaimAttachedFile => ({
    attachedId: file.name,
    attachedHash: toBase64Hash(file.id),
});

const toBase64Hash = (hexHash: string): string => {
//     console.log("old", hexHash);
    const trimmedHash = hexHash.trim();
    const normalizedHash = trimmedHash.toLowerCase().startsWith("0x")
        ? trimmedHash.slice(2).toLowerCase()
        : trimmedHash.toLowerCase();
    if (!/^[0-9a-f]{64}$/.test(normalizedHash)) {
//         console.log("invalid hash", trimmedHash);
        return ZERO_ATTACHMENT_HASH;
    }

    let res = btoa(
        normalizedHash
            .match(/.{2}/g)!
            .map((byte) => String.fromCharCode(parseInt(byte, 16)))
            .join(""),
    );
//     console.log("new", res);
    return res;
};

export const useClaimList = (
    projectLinearId: UUID,
): SWRResponse<BesuClaimListItem[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BesuClaimListItem[], ApiError>(
        `/claims/list/${projectLinearId}`,
        request,
        { onError },
    );
};

export const useClaim = (
    claimOnChainId: string,
): SWRResponse<BesuClaimState, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BesuClaimState, ApiError>(
        `/claims/detail/${claimOnChainId}`,
        request,
        { onError },
    );
};

export const usePaymentOrder = (
    linearId: UUID,
): SWRResponse<StateAndRef<ClaimState>, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<StateAndRef<ClaimState>, ApiError>(
        `/payment/linearId/${linearId}/latest`,
        request,
        { onError },
    );
};

export const useClaimHistory = (
    linearId: UUID,
): SWRResponse<ClaimHistory, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<ClaimHistory, ApiError>(
        `/claims/history/${linearId}`,
        request,
        { onError },
    );
};

export const useClaimWithToken = (
    linearId: UUID,
): SWRResponse<StateAndRef<ClaimState>, ApiError> => {
    const { token } = useAuth();
    return useSWR<StateAndRef<ClaimState>, ApiError>(
        token ? `/token-access/claims/linearId/${linearId}/latest` : null,
        requestWithToken(token),
    );
};

export const useClaimHistoryWithToken = (
    linearId: UUID,
): SWRResponse<StateAndRef<ClaimState>[], ApiError> => {
    const { token } = useAuth();
    return useSWR<StateAndRef<ClaimState>[], ApiError>(
        token ? `/token-access/claims/linearId/${linearId}/history` : null,
        requestWithToken(token),
    );
};

type ClaimCreationRequest = {
    projectId: UUID;
    descriptionRef: string;
    attachedFiles: ClaimAttachedFile[];
};

type ClaimAttachedFile = {
    attachedId: string;
    attachedHash: string;
};

type ClaimUpdateRequest = {
    description: string;
    documents: ClaimAttachedFile[];
};

type ClaimReturnRequest = {
    comment: string;
};

type ClaimWorkflowResult = WorkflowResult<{
    ClaimState: StateAndRef<ClaimState>[];
}> & {
    stateRefMap?: {
        referenceId?: UUID[];
    };
};
type BesuClaimWorkflowResult = WorkflowResult<{
    BesuClaimState: StateAndRef<BesuClaimState>[];
}> & {
    stateRefMap?: {
        onChainId?: UUID[];
    };
};

export default claimApi;
